import React, { useEffect, useState } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, ShieldCheckIcon, LockIcon, AlertTriangleIcon, ArchiveIcon, CheckCircleIcon, BanIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { Role } from '../../types';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
type RoleStatus = 'active' | 'inactive' | 'archived';
interface RoleWithStatus extends Role {
  status: RoleStatus;
}
export function Roles() {
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const {
    roles,
    addRole,
    updateRole,
    deleteRole
  } = useData();
  const [rolesWithStatus, setRolesWithStatus] = useState<RoleWithStatus[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<RoleWithStatus[]>([]);
  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<RoleStatus | 'all'>('active');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleWithStatus | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  // Load role statuses from localStorage
  const getRoleStatuses = (): Record<string, RoleStatus> => {
    try {
      const saved = localStorage.getItem('savidna-role-statuses');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  };
  const saveRoleStatus = (roleId: string, status: RoleStatus) => {
    const statuses = getRoleStatuses();
    statuses[roleId] = status;
    localStorage.setItem('savidna-role-statuses', JSON.stringify(statuses));
  };
  // Initialize roles with status
  useEffect(() => {
    const statuses = getRoleStatuses();
    const mapped = roles.map(role => ({
      ...role,
      status: statuses[role.id] || 'active'
    }));
    setRolesWithStatus(mapped);
  }, [roles]);
  // Filter logic
  useEffect(() => {
    let result = rolesWithStatus;
    if (statusFilter !== 'all') {
      result = result.filter(r => r.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(r => r.displayName.toLowerCase().includes(term) || r.name.toLowerCase().includes(term) || (r.description || '').toLowerCase().includes(term));
    }
    setFilteredRoles(result);
  }, [rolesWithStatus, statusFilter, searchTerm]);
  const handleStatusChange = (role: RoleWithStatus, newStatus: RoleStatus) => {
    if (role.isSystemRole) {
      showToast({
        type: 'warning',
        title: 'Acción no permitida',
        message: 'No se pueden modificar los roles del sistema.'
      });
      return;
    }
    let title = '';
    let message = '';
    let confirmText = '';
    let variant: 'primary' | 'danger' | 'warning' | 'info' = 'primary';
    if (newStatus === 'archived') {
      title = 'Archivar Rol';
      message = `¿Estás seguro de que deseas archivar el rol "${role.displayName}"?`;
      confirmText = 'Archivar';
      variant = 'warning';
    } else if (newStatus === 'inactive') {
      title = 'Desactivar Rol';
      message = `¿Estás seguro de que deseas desactivar el rol "${role.displayName}"?`;
      confirmText = 'Desactivar';
      variant = 'warning';
    } else if (newStatus === 'active') {
      title = 'Activar Rol';
      message = `¿Estás seguro de que deseas activar el rol "${role.displayName}"?`;
      confirmText = 'Activar';
      variant = 'primary';
    }
    confirm({
      title,
      message,
      confirmText,
      variant,
      onConfirm: () => {
        saveRoleStatus(role.id, newStatus);
        setRolesWithStatus(prev => prev.map(r => r.id === role.id ? {
          ...r,
          status: newStatus
        } : r));
        // Persist to Supabase
        updateRole(role.id, {
          status: newStatus
        } as any).catch(e => logger.error('Error updating role status in Supabase:', e));
      }
    });
  };
  const openNewForm = () => {
    setSelectedRole(null);
    setFormData({
      name: '',
      displayName: '',
      description: ''
    });
    setFormErrors({});
    setSuccessMessage('');
    setIsFormOpen(true);
  };
  const openEditForm = (role: RoleWithStatus) => {
    if (role.isSystemRole) return;
    setSelectedRole(role);
    setFormData({
      name: role.name,
      displayName: role.displayName,
      description: role.description || ''
    });
    setFormErrors({});
    setSuccessMessage('');
    setIsFormOpen(true);
  };
  const openDeleteConfirm = (role: RoleWithStatus) => {
    if (role.isSystemRole) return;
    setSelectedRole(role);
    setIsDeleteConfirmOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    setSuccessMessage('');
    // Validation
    const errors: Record<string, string> = {};
    if (!formData.displayName.trim()) errors.displayName = 'El nombre visible es requerido';
    if (!selectedRole && !formData.name.trim()) errors.name = 'El nombre interno es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    confirm({
      title: selectedRole ? 'Guardar Cambios' : 'Crear Rol',
      message: selectedRole ? '¿Estás seguro de que deseas guardar los cambios de este rol?' : '¿Estás seguro de que deseas crear este nuevo rol?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        setIsSubmitting(true);
        try {
          if (selectedRole) {
            await updateRole(selectedRole.id, {
              displayName: formData.displayName,
              description: formData.description
            });
            showToast({
              type: 'success',
              title: 'Rol actualizado',
              message: `${formData.displayName} fue actualizado correctamente.`
            });
          } else {
            await addRole({
              name: formData.name.toLowerCase().replace(/\s+/g, '_'),
              displayName: formData.displayName,
              description: formData.description,
              isSystemRole: false
            });
            showToast({
              type: 'success',
              title: 'Rol creado',
              message: `${formData.displayName} fue creado correctamente.`
            });
          }
          setIsFormOpen(false);
        } catch (error) {
          logger.error('Error saving role:', error);
          showToast({
            type: 'error',
            title: 'Error al guardar',
            message: 'No se pudo guardar el rol.'
          });
          setFormErrors({
            submit: 'Error al guardar el rol'
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    });
  };
  const handleDelete = async () => {
    if (!selectedRole) return;
    setIsSubmitting(true);
    try {
      const roleName = selectedRole.displayName || selectedRole.name;
      await deleteRole(selectedRole.id);
      showToast({
        type: 'error',
        title: 'Rol eliminado',
        message: `${roleName} fue eliminado.`
      });
      setIsDeleteConfirmOpen(false);
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Error al eliminar',
        message: 'No se pudo eliminar el rol.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const getStatusLabel = (status: RoleStatus) => {
    switch (status) {
      case 'active':
        return 'Activo';
      case 'inactive':
        return 'Inactivo';
      case 'archived':
        return 'Archivado';
    }
  };
  return <div className="space-y-6" data-id="element-5418" data-mp="28mqeifb1p645">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-5419" data-mp="28pbgd4bcfm1w">
        <div data-id="element-5420" data-mp="2wf5s1ijxf4kk">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5421" data-mp="2wffb0m6k0bgr">
            <ShieldCheckIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-5422" data-mp="31e0csq1gf37b" />
            Roles
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5423" data-mp="2rluivarn6bph">
            Define los roles y niveles de acceso para los usuarios
          </p>
        </div>
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-5425" data-mp="3k2f135ke034f" />} onClick={openNewForm} data-id="element-5424" data-mp="3f9u989l1k03j">
          Nuevo Rol
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-4" data-id="element-5426" data-mp="18lvaaid4qlgb">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between" data-id="element-5427" data-mp="13olcoffl73he">
          <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-full md:w-auto" data-id="element-5428" data-mp="13mr1hsu4apqi">
            {(['active', 'inactive', 'archived', 'all'] as const).map(status => <button key={status} onClick={() => setStatusFilter(status)} className={`
                  px-4 py-1.5 text-sm font-medium rounded-md transition-all flex-1 md:flex-none capitalize
                  ${statusFilter === status ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}
                `} data-id="element-5429" data-mp="1dgr2biid98tf">
                  {status === 'all' ? 'Todos' : getStatusLabel(status)}
                </button>)}
          </div>

          <div className="relative w-full md:w-64" data-id="element-5430" data-mp="37ss6bgc8p19v">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-5431" data-mp="37rh7luxnijpm" />
            <input type="text" placeholder="Buscar roles..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all" data-id="element-5432" data-mp="2y2h3xy87f053" />
          </div>
        </div>
      </Card>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-5433" data-mp="2f5fvu4jcsjwx">
        {filteredRoles.length === 0 ? <Card className="col-span-full" data-id="element-5434" data-mp="dbzsq862xcl2">
            <div className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-5435" data-mp="dbq1kh9aszds">
              No se encontraron roles con los filtros seleccionados.
            </div>
          </Card> : filteredRoles.map(role => <Card key={role.id} className={`hover:shadow-lg transition-all duration-300 group ${role.status === 'archived' ? 'opacity-75 grayscale-[0.5]' : ''}`} data-id="element-5436" data-mp="kvr99t2sd9wt">
              <div className="p-6 flex flex-col h-full" data-id="element-5437" data-mp="ulkgkhvwb2v9">
                <div className="flex items-start justify-between mb-4" data-id="element-5438" data-mp="ul12jc9d8ymi">
                  <div className="flex items-center gap-3" data-id="element-5439" data-mp="3t49c3084aox3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm ${role.isSystemRole ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' : 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'}`} data-id="element-5440" data-mp="1wwtcgrl63f2">
                      <ShieldCheckIcon className="w-6 h-6" data-id="element-5441" data-mp="1ibet0ycaypez" />
                    </div>
                    <div data-id="element-5442" data-mp="1na7yc3hfra42">
                      <h3 className="font-bold text-gray-900 dark:text-white text-lg" data-id="element-5443" data-mp="1n9ge1otagyt3">
                        {role.displayName}
                      </h3>
                      <div className="flex items-center gap-2" data-id="element-5444" data-mp="3jfgyqfhaxfl8">
                        <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-gray-500" data-id="element-5445" data-mp="3edhyheguyysi">
                          {role.name}
                        </code>
                        {role.status !== 'active' && <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${role.status === 'inactive' ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600'}`} data-id="element-5446" data-mp="3oamj39v22m77">
                            {getStatusLabel(role.status)}
                          </span>}
                      </div>
                    </div>
                  </div>
                  {role.isSystemRole && <div title="Rol del sistema" data-id="element-5447" data-mp="2gwszdsjamamt">
                      <LockIcon className="w-4 h-4 text-gray-400" data-id="element-5448" data-mp="2cy3n639zqz4r" />
                    </div>}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 flex-1 leading-relaxed" data-id="element-5449" data-mp="22rsktr5ldhur">
                  {role.description || 'Sin descripción disponible para este rol.'}
                </p>

                <div className="pt-4 border-t border-gray-100 dark:border-gray-700/50 flex items-center gap-2" data-id="element-5450" data-mp="1stcw1uu1ekof">
                  {role.isSystemRole ? <div className="w-full text-center text-xs text-gray-400 italic py-2" data-id="element-5451" data-mp="2ll6c9ma3j65m">
                      Rol protegido del sistema
                    </div> : <>
                      <button onClick={() => openEditForm(role)} className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors flex-1 flex justify-center" title="Editar" data-id="element-5452" data-mp="2bl5rrx0hqjzu">
                        <EditIcon className="w-4 h-4" data-id="element-5453" data-mp="1lcrffs47opti" />
                      </button>

                      {role.status === 'active' ? <button onClick={() => handleStatusChange(role, 'inactive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors flex-1 flex justify-center" title="Desactivar" data-id="element-5454" data-mp="1gkzz0aers0h3">
                          <BanIcon className="w-4 h-4" data-id="element-5455" data-mp="1zcj02bp8h4gh" />
                        </button> : <button onClick={() => handleStatusChange(role, 'active')} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors flex-1 flex justify-center" title="Activar" data-id="element-5456" data-mp="1ucwmu29eno7o">
                          <CheckCircleIcon className="w-4 h-4" data-id="element-5457" data-mp="e1ajx5osvkum" />
                        </button>}

                      {role.status !== 'archived' ? <button onClick={() => handleStatusChange(role, 'archived')} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-1 flex justify-center" title="Archivar" data-id="element-5458" data-mp="2ume3utetmox2">
                          <ArchiveIcon className="w-4 h-4" data-id="element-5459" data-mp="3dbu89rtmn1ir" />
                        </button> : <button onClick={() => openDeleteConfirm(role)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-1 flex justify-center" title="Eliminar permanentemente" data-id="element-5460" data-mp="3iaxgfl4wlnuy">
                          <TrashIcon className="w-4 h-4" data-id="element-5461" data-mp="1sy3oyy7l3y10" />
                        </button>}
                    </>}
                </div>
              </div>
            </Card>)}
      </div>

      {/* Role Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => !isSubmitting && setIsFormOpen(false)} title={selectedRole ? 'Editar Rol' : 'Nuevo Rol'} size="md" icon={<ShieldCheckIcon className="w-5 h-5" data-id="element-5463" data-mp="23t49ijml2gmp" />} accentColor="purple" data-id="element-5462" data-mp="1uvbr36lkesv3">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-5464" data-mp="1zrshy8k5flbv">
          {successMessage && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm flex items-center gap-2" data-id="element-5465" data-mp="2dki5ihfbsyb3">
              <ShieldCheckIcon className="w-4 h-4" data-id="element-5466" data-mp="2nhmt8lotl1u0" />
              {successMessage}
            </div>}

          <div data-id="element-5467" data-mp="2ike7kb4gqfo0">
            <div className="flex items-center gap-2 mb-4" data-id="element-5468" data-mp="2ikwdjzgxg894">
              <span className="w-1.5 h-5 rounded-full bg-purple-500" data-id="element-5469" data-mp="324ljidbxxq75"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-5470" data-mp="366q9n236k4p1">
                Identificación
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-5471" data-mp="2s6pabr2kd5bo">
              {!selectedRole && <Input label="Nombre Interno (único)" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="ej: gerente_ventas" helperText="Solo letras minúsculas, números y guiones bajos" disabled={isSubmitting} data-id="element-5472" data-mp="2ig2xg4m8eswo" />}
              <Input label="Nombre Visible" value={formData.displayName} onChange={e => setFormData({
              ...formData,
              displayName: e.target.value
            })} error={formErrors.displayName} placeholder="ej: Gerente de Ventas" disabled={isSubmitting} data-id="element-5473" data-mp="24j7cbdsphb09" />
            </div>
          </div>

          <div data-id="element-5474" data-mp="211tdinkqii7z">
            <div className="flex items-center gap-2 mb-4" data-id="element-5475" data-mp="212cxe9ty3gon">
              <span className="w-1.5 h-5 rounded-full bg-indigo-500" data-id="element-5476" data-mp="1w909y3105fxh"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-5477" data-mp="1s7f0krnofjs1">
                Detalles
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-5478" data-mp="1ia0unlzdqddz">
              <Textarea label="Descripción del Rol" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} placeholder="Describe las responsabilidades de este rol..." disabled={isSubmitting} data-id="element-5479" data-mp="1dafmckybksol" />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-5480" data-mp="kgrharpmqgdg">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} disabled={isSubmitting} data-id="element-5481" data-mp="pf2qd083omum">
              Cancelar
            </Button>
            <Button type="submit" isLoading={isSubmitting} data-id="element-5482" data-mp="z6gtdhz9g4si">
              {selectedRole ? 'Guardar Cambios' : 'Crear Rol'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => !isSubmitting && setIsDeleteConfirmOpen(false)} title="Eliminar Rol" size="sm" icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-5484" data-mp="23v8u0vsdgryn" />} accentColor="rose" data-id="element-5483" data-mp="6na03dkf1fql">
        <div className="space-y-4" data-id="element-5485" data-mp="28vd9cihjngna">
          <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg" data-id="element-5486" data-mp="28welyre7kb74">
            <AlertTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" data-id="element-5487" data-mp="2cwfcvbihuev2" />
            <div data-id="element-5488" data-mp="1g4uzmymbeh0w">
              <p className="text-sm font-medium text-red-800 dark:text-red-200" data-id="element-5489" data-mp="1l627sysddi83">
                ¿Estás seguro de que deseas eliminar este rol?
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-5490" data-mp="1p62ypiwnnlw1">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2" data-id="element-5491" data-mp="xh9hbhuio2kv">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} disabled={isSubmitting} data-id="element-5492" data-mp="xenbbmn9xv4r">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={isSubmitting} data-id="element-5493" data-mp="ozfxu737zju9">
              Eliminar Rol
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}